import React, {useEffect, useState} from 'react';
import {apiErrorMessage} from '../api/abfApi';
import {remedyApi, type AmendmentImpact} from './forwardFlowRemedyApi';
import {shortDate, usd} from './forwardFlowServicingApi';
import {optionWords, TERM_OPTIONS, termLabel, word} from './forwardFlowWords';
import {Callout, Scroll, StateChip, Table} from './ForwardFlowServicingStyles';

const shown = (term: string, value?: string | null) => !value ? '—' : TERM_OPTIONS[term] ? optionWords(term, value)
  : value === 'true' ? 'Permitted' : value === 'false' ? 'Not permitted' : value;

/**
 * What this version changes against the active agreement, and which open work each
 * version governs (FF-RM06, AC25). A binding decision keeps the version it bound;
 * activation applies the new terms only from the contract's effective boundary.
 */
export function ForwardFlowAmendmentImpact({programId, versionId}: {programId: number; versionId: number}) {
  const [impact, setImpact] = useState<AmendmentImpact | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    remedyApi.amendmentImpact(programId, versionId)
      .then(i => { if (live) { setImpact(i); setError(''); } })
      .catch(e => { if (live) setError(apiErrorMessage(e, 'The comparison with the agreement in force could not be loaded.')); });
    return () => { live = false; };
  }, [programId, versionId]);
  if (error) return <Callout $tone="warn">{error}</Callout>;
  if (!impact) return <p className="muted">Comparing against the active agreement…</p>;
  if (!impact.active) return <p className="muted">{impact.note}</p>;
  return <div style={{border: '1px solid #2c4258', borderRadius: 10, padding: 12, margin: '12px 0'}}>
    <h3 style={{margin: '0 0 4px', fontSize: 14}}>What this version changes against version {impact.active.versionNumber}, now in force</h3>
    <p style={{margin: '0 0 8px', color: '#9fb1c6', fontSize: 13}}>{impact.note}</p>
    {impact.changes.length === 0 ? <p className="muted">No term differs from the active agreement.</p> :
    <Scroll><Table style={{maxWidth: 760}}>
      <thead><tr><th>Term</th><th>In force</th><th>This version</th><th>Effect</th></tr></thead>
      <tbody>{impact.changes.map(c => <tr key={c.term}>
        <td>{termLabel(c.term)}</td><td>{shown(c.term, c.from)}</td><td>{shown(c.term, c.to)}</td>
        <td>{c.material ? 'Changes what may be bought or paid' : 'Wording only'}</td></tr>)}</tbody>
    </Table></Scroll>}
    {(impact.openBatches?.length ?? 0) > 0 && <>
      <h4 style={{margin: '10px 0 4px', fontSize: 13}}>Open offers and the version each is decided under</h4>
      <Scroll><Table style={{maxWidth: 760}}>
        <thead><tr><th>Seller reference</th><th>Offer</th><th>Purchase</th><th>Decided under</th></tr></thead>
        <tbody>{impact.openBatches!.map(b => <tr key={b.id}>
          <td>{b.seller_reference} · revision {b.revision_number}</td><td>{word('source', b.state).label}</td>
          <td>{b.decision_id ? word('decision', b.decision_state).label : 'Not yet sent for approval'}</td>
          <td>{b.governing_agreement_id === impact.active!.id ? `Version ${impact.active!.versionNumber} · keeps its terms` : 'This version'}</td>
        </tr>)}</tbody></Table></Scroll></>}
    {(impact.openObligations?.length ?? 0) > 0 && <>
      <h4 style={{margin: '10px 0 4px', fontSize: 13}}>Payments still owed to the seller (binding; this version does not change them)</h4>
      <Scroll><Table style={{maxWidth: 600}}>
        <thead><tr><th>Purchase</th><th>Status</th><th className="num">Still owed</th></tr></thead>
        <tbody>{impact.openObligations!.map(o => <tr key={o.acquisition_id}>
          <td>Purchase {o.acquisition_id}</td><td><StateChip state={o.state} /></td><td className="num">{usd(o.payable_remaining)}</td></tr>)}</tbody>
      </Table></Scroll></>}
    <p style={{margin: '8px 0 0', fontSize: 12, color: '#8ea3ba'}}>
      Takes effect: {impact.proposed.effectiveFrom ? shortDate(impact.proposed.effectiveFrom) : 'set the effective date in the terms before activation'}.
    </p>
  </div>;
}
